import { Skeleton, Tooltip } from 'antd';
import { DateTime } from 'luxon';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AccountActions, TransactionsActions } from '@portal/actions';
import { extract } from '@portal/config/app.json';
import { DATE_FILTER_TIME_INTERVAL } from '@portal/config/env';
import { TransactionFilter } from '@portal/models/advanced-filters';
import {
  ExportExtractMethod,
  StatementFileType,
  TransactionFlow,
} from '@portal/models/enumerators';
import { setHeader } from '@portal/repositories/securitySdkConfig';
import { translate } from '@portal/services/i18n';

import AccessListControl from '@portal/components/AccessListControl/AccessListControl';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedHeaderDate from '@portal/components/AdvancedHeaderDate/AdvancedHeaderDate';
import AdvancedRangePicker from '@portal/components/AdvancedRangePicker/AdvancedRangePicker';
import ExportExtractModal from '@portal/components/ExportExtractModal/ExportExtractModal';
import TransactionList from '@portal/components/TransactionList/TransactionList';
import TransactionListSkeleton from '@portal/components/TransactionList/TransactionListSkeleton';
import RolesType from '@portal/enum/rolesType';
import useReduxState from '@portal/hooks/useReduxState';
import { StorageItems } from '@portal/services/storage';

import {
  CalendarOutlined,
  ClearOutlined,
  CloseCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTheme } from '@material-ui/core';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import { getRouteStackPath } from '@portal/config/routes';

import { useNavigation } from '@portal/hooks/useNavigation';

const ExtractPage = () => {
  const navigate = useNavigation();
  const { palette } = useTheme();

  const defaultTransactionFilter: TransactionFilter = {
    flow: undefined,
    startAt: undefined,
    endAt: undefined,
    pageSize: 10,
    nroUltMovimentoEnv: undefined,
  };

  const dispatch = useDispatch();
  const {
    transactions: { transactions, scheduledTransactions },
    loading: { loadTransactionList },
    account: { exportExtractDownloadUrl },
    paymentAccount: { selectedPaymentAccount },
    user: {
      me: {
        account: { createdAt },
      },
    },
    theme: { currentTheme },
  } = useReduxState();

  const [displayType, setDisplayType] = useState(TransactionFlow.ALL);
  const [modalExtract, setModalExtract] = useState(false);
  const [transactionFilters, setTransactionFilters] =
    useState<TransactionFilter>(defaultTransactionFilter);
  const [datePicker, setDatePicker] = useState<[Moment, Moment] | null>(null);
  const [lastTransactionId, setLastTransactionId] = useState<
    string | undefined
  >(undefined);
  const [loadingMoreTransactions, setLoadingMoreTransactions] =
    useState<boolean>(false);
  const [showDateRange, setShowDateRange] = useState(false);

  const handleChangeDatePiker = (values: Array<Moment | null> | null) => {
    if (values && values[0] && values[1]) {
      const startAt = values[0]
        ?.utc(true)
        .startOf('day')
        .toDate()
        .toISOString();
      const endAt = values[1]?.utc(true).endOf('day').toDate().toISOString();

      setTransactionFilters((oldFilters) => ({
        ...oldFilters,
        startAt,
        endAt,
      }));
      setDatePicker([values[0], values[1]]);
      sessionStorage.setItem(
        StorageItems.EXTRACT_FILTER_STORAGE_KEY,
        JSON.stringify({ startAt, endAt })
      );
    }
  };

  const getAllTransactionsFiltered = useCallback(() => {
    if (
      !transactionFilters.startAt ||
      !transactionFilters.endAt ||
      displayType === TransactionFlow.SCHEDULED
    ) {
      return;
    }

    setLastTransactionId(undefined);

    const transactionParams = {
      ...transactionFilters,
      ...(displayType !== TransactionFlow.ALL && { flow: displayType }),
      nroUltMovimentoEnv: undefined,
    };

    dispatch(
      TransactionsActions.getAllTransactions(
        transactionParams,
        loadingMoreTransactions
      )
    );

    dispatch(TransactionsActions.getDailyBalance(transactionParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, displayType, transactionFilters]);

  const loadMoreTransactions = useCallback(() => {
    if (
      !transactionFilters.startAt ||
      !transactionFilters.endAt ||
      displayType === TransactionFlow.SCHEDULED
    ) {
      return;
    }

    setLoadingMoreTransactions(true);

    const transactionParams = {
      ...transactionFilters,
      ...(displayType !== TransactionFlow.ALL && { flow: displayType }),
      nroUltMovimentoEnv: lastTransactionId,
    };

    dispatch(TransactionsActions.getAllTransactions(transactionParams, true));

    dispatch(TransactionsActions.getDailyBalance(transactionParams));
  }, [dispatch, displayType, transactionFilters, lastTransactionId]);

  useEffect(() => {
    if (selectedPaymentAccount) {
      setHeader(
        StorageItems.PAYMENT_ACCOUNT_ID,
        selectedPaymentAccount.id,
        'session'
      );
    }
  }, [dispatch, selectedPaymentAccount]);

  useEffect(() => {
    const saved = sessionStorage.getItem(
      StorageItems.EXTRACT_FILTER_STORAGE_KEY
    );

    if (saved) {
      const { startAt, endAt } = JSON.parse(saved);
      setTransactionFilters((oldFilters) => ({
        ...oldFilters,
        startAt,
        endAt,
      }));
      setDatePicker([moment(startAt), moment(endAt)]);
      return;
    }

    const startDate = DateTime.now()
      .minus({ days: DATE_FILTER_TIME_INTERVAL || 30 })
      .startOf('day');

    const endDate = DateTime.now()
      .plus({
        days: 5,
      })
      .endOf('day');

    const startAt = startDate.toISODate();
    const endAt = endDate.toISODate();

    setTransactionFilters((oldFilters) => ({
      ...oldFilters,
      startAt,
      endAt,
    }));

    setDatePicker([moment(startDate.toISO()), moment(endDate.toISO())]);
  }, []);

  useEffect(() => {
    if (exportExtractDownloadUrl) {
      const files: string[] = [];

      if (exportExtractDownloadUrl?.pdf) {
        files.push(exportExtractDownloadUrl.pdf);
      }

      if (exportExtractDownloadUrl?.csv) {
        files.push(exportExtractDownloadUrl.csv);
      }

      if (exportExtractDownloadUrl?.ofx) {
        files.push(exportExtractDownloadUrl.ofx);
      }

      if (files.length > 0) {
        let currentIndex = 0;
        const intervalId = setInterval(() => {
          if (currentIndex === files.length - 1) clearInterval(intervalId);
          window.location.href = files[currentIndex];
          currentIndex++;
        }, 1000);
      }

      dispatch(AccountActions.cleanExportDownloadUrl());
    }
  }, [exportExtractDownloadUrl, dispatch]);

  useEffect(() => {
    if (displayType === TransactionFlow.SCHEDULED && !scheduledTransactions) {
      dispatch(TransactionsActions.getScheduledTransactions());
    }
  }, [displayType, scheduledTransactions, dispatch]);

  const handleSubmit = (
    method: ExportExtractMethod,
    startAt: string,
    endAt: string,
    order: 'asc' | 'desc',
    fileType: StatementFileType
  ) => {
    dispatch(
      AccountActions.getExportExtract(method, startAt, endAt, order, fileType)
    );

    setModalExtract(false);
  };

  useEffect(() => {
    getAllTransactionsFiltered();
  }, [getAllTransactionsFiltered]);

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('/detalhes/')) {
        sessionStorage.removeItem(StorageItems.EXTRACT_FILTER_STORAGE_KEY);
      }
    };
  }, []);

  useEffect(() => {
    if (transactions && transactions.length > 0) {
      const lastTransaction = transactions[transactions.length - 1];
      if (lastTransaction && 'bankId' in lastTransaction) {
        setLastTransactionId(lastTransaction.bankId);
      }

      setLoadingMoreTransactions(false);
    }
  }, [transactions, transactionFilters.pageSize]);

  const currentTransactions =
    displayType === TransactionFlow.SCHEDULED
      ? scheduledTransactions
      : transactions;
  const isEmpty = !currentTransactions || currentTransactions.length === 0;

  const extractButtons = [
    {
      text: translate('PAGES.EXTRACT.BUTTONS.ALL'),
      disabled: loadTransactionList,
      onClick: () => setDisplayType(TransactionFlow.ALL),
      variant: displayType === TransactionFlow.ALL ? 'contained' : 'text',
      textColor:
        displayType === TransactionFlow.ALL
          ? palette.secondary.dark
          : palette.primary.main,
    },
    {
      text: translate('PAGES.EXTRACT.BUTTONS.CASH_IN'),
      disabled: loadTransactionList,
      onClick: () => setDisplayType(TransactionFlow.CASH_IN),
      variant: displayType === TransactionFlow.CASH_IN ? 'contained' : 'text',
      textColor:
        displayType === TransactionFlow.CASH_IN
          ? palette.secondary.dark
          : palette.primary.main,
    },
    {
      text: translate('PAGES.EXTRACT.BUTTONS.CASH_OUT'),
      disabled: loadTransactionList,
      onClick: () => setDisplayType(TransactionFlow.CASH_OUT),
      variant: displayType === TransactionFlow.CASH_OUT ? 'contained' : 'text',
      textColor:
        displayType === TransactionFlow.CASH_OUT
          ? palette.secondary.dark
          : palette.primary.main,
    },
    {
      text: translate('PAGES.EXTRACT.BUTTONS.SCHEDULED'),
      disabled: loadTransactionList,
      onClick: () => setDisplayType(TransactionFlow.SCHEDULED),
      variant: displayType === TransactionFlow.SCHEDULED ? 'contained' : 'text',
      textColor:
        displayType === TransactionFlow.SCHEDULED
          ? palette.secondary.light
          : palette.primary.main,
    },
  ];

  const clearFilters = () => {
    const startDate = DateTime.now()
      .minus({ days: DATE_FILTER_TIME_INTERVAL || 30 })
      .startOf('day');

    const endDate = DateTime.now().plus({ days: 5 }).endOf('day');

    const startAt = startDate.toISODate();
    const endAt = endDate.toISODate();

    setTransactionFilters({
      flow: undefined,
      nroUltMovimentoEnv: undefined,
      pageSize: 10,
      startAt,
      endAt,
    });

    setDatePicker([moment(startDate.toISO()), moment(endDate.toISO())]);
    sessionStorage.removeItem(StorageItems.EXTRACT_FILTER_STORAGE_KEY);

    setLastTransactionId(undefined);

    const params = {
      ...defaultTransactionFilter,
      startAt,
      endAt,
      ...(displayType !== TransactionFlow.ALL && { flow: displayType }),
    };

    dispatch(TransactionsActions.getAllTransactions(params, false));
    dispatch(TransactionsActions.getDailyBalance(params));
  };

  const renderCalendar = () => {
    let text = translate('PAGES.EXTRACT.BUTTONS.OPEN_CALENDAR');
    let icon = <CalendarOutlined />;

    if (showDateRange) {
      text = translate('PAGES.EXTRACT.BUTTONS.CLOSE_CALENDAR');
      icon = <CloseCircleOutlined />;
    }

    return (
      <Tooltip title={text} placement="top">
        <div
          className={`extract-page__calendar ${showDateRange ? 'open' : ''}`}
          onClick={() => setShowDateRange((prevVisible) => !prevVisible)}
        >
          {icon}
        </div>
      </Tooltip>
    );
  };

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: false,
            title: translate('PAGES.EXTRACT.TITLE'),
          },
        ]}
      />
      <div className="extract-page">
        <AdvancedPanelContentHeader
          title={translate('PAGES.EXTRACT.TITLE')}
          testId="extract-page-header"
          description={translate('PAGES.EXTRACT.DESCRIPTION')}
          goBackAction={() => navigate(getRouteStackPath('HOME', 'HOME_PAGE'))}
          buttonTitle={translate('APPLICATION.BACK_TO_INIT')}
        />
        <div className="extract-page__card">
          <div className="extract-page__header">
            <div />
            {!loadTransactionList ? (
              extract.exportExtract && (
                <AccessListControl
                  NoAccessComponent={<Skeleton.Button />}
                  accessRole={RolesType.TRANSACTION_READ}
                >
                  <AdvancedButton
                    customStyles={{ width: 200 }}
                    text={translate('PAGES.EXTRACT.EXPORT.BUTTON')}
                    onClick={() => {
                      setModalExtract(true);
                    }}
                    variant="outlined"
                  />
                </AccessListControl>
              )
            ) : (
              <Skeleton.Button active />
            )}
          </div>
          <div
            className="extract-page__tabs"
            style={{ borderBottom: `1px solid ${currentTheme.borderColor}` }}
          >
            {extractButtons.map((button) => (
              <button
                className={`extract-page__tabs__button ${
                  button.variant === 'contained' ? 'active' : ''
                }`}
                key={button.text}
                type="button"
                onClick={button.onClick}
                disabled={button.disabled}
                style={{
                  color: button.textColor,
                }}
              >
                {button.text}
              </button>
            ))}
          </div>
          <div className="extract-page__filters">
            <AdvancedHeaderDate
              initialDate={transactionFilters.startAt ?? ''}
              finalDate={transactionFilters.endAt ?? ''}
              longRangeSelectorFormat={extract.longRangeSelectorFormat}
            />
            <div className="extract-page__filters__row">
              {showDateRange ? (
                <div className="extract-page__calendar__wrapper">
                  <AdvancedRangePicker
                    onChange={(value) => handleChangeDatePiker(value)}
                    value={datePicker}
                    disabled={!!loadTransactionList}
                    onFocus={() => setDatePicker(null)}
                    placeholder={[
                      DateTime.now()
                        .minus({ days: DATE_FILTER_TIME_INTERVAL || 30 })
                        .startOf('day')
                        .toFormat(
                          translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
                        ),
                      DateTime.now()
                        .plus({ days: 5 })
                        .endOf('day')
                        .toFormat(
                          translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
                        ),
                    ]}
                  />
                </div>
              ) : null}
              {renderCalendar()}
              <AdvancedButton
                text={translate('PAGES.EXTRACT.BUTTONS.SEARCH')}
                onClick={() => getAllTransactionsFiltered()}
                disabled={loadTransactionList}
                endIcon={<SearchOutlined />}
                customStyles={{
                  width: '200px',
                }}
              />
              <AdvancedButton
                text={translate('PAGES.EXTRACT.BUTTONS.CLEAN_FILTERS')}
                onClick={() => clearFilters()}
                endIcon={<ClearOutlined />}
                variant="outlined"
                customStyles={{
                  width: '200px',
                }}
              />
            </div>
          </div>
          <div className="extract-page__content">
            <AccessListControl
              NoAccessComponent={<TransactionListSkeleton active={false} />}
              accessRole={RolesType.TRANSACTION_READ}
            >
              <TransactionList
                longRangeSelectorFormat={extract.longRangeSelectorFormat}
                transactionTime={extract.transactionTime}
                transactionIcon={extract.transactionIcon}
                transactionDetails={extract.transactionIcon}
                isScheduled={displayType === TransactionFlow.SCHEDULED}
                isExtract
                data={
                  displayType === TransactionFlow.SCHEDULED
                    ? scheduledTransactions
                    : transactions
                }
                loading={!!loadTransactionList}
              />
            </AccessListControl>
          </div>
          {!isEmpty && (
            <div className="extract-page__load-more">
              <AdvancedButton
                text={translate('PAGES.EXTRACT.BUTTONS.LOAD_MORE')}
                onClick={loadMoreTransactions}
                disabled={loadingMoreTransactions}
                loading={loadingMoreTransactions}
                variant="outlined"
              />
            </div>
          )}
        </div>
      </div>
      <ExportExtractModal
        visible={modalExtract}
        setModalExtract={(value: boolean) => setModalExtract(value)}
        handleSubmit={handleSubmit}
        createdAt={createdAt ?? ''}
      />
    </>
  );
};

export default ExtractPage;
